import React, { useState, useEffect, useCallback } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import EmptyState from '../components/tw1/EmptyState';
import { BookOpen, Search, ChevronUp, ChevronDown, Filter, X, Download, FileDown, CheckSquare, Square } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';
import { TW1_LOGO } from '../lib/logos';

const MODULE_COLORS = {
  'SEPA Betaalbatch': '#ff6200',
  'Maandafsluiting': '#003366',
  "Incasso's Exact": '#1e8c45',
  'Betalingen Exact': '#526071',
  'Salaris': '#ff8f00',
  'Storno Incasso': '#c0392b',
  'PTW Fac. Import': '#003366',
  'Truckshop 1 Memo': '#e91e8c',
};

export default function LogboekPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('datum');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [moduleStats, setModuleStats] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/logs', {
        params: { search, sort_by: sortBy, sort_dir: sortDir, page, limit: 20, module: moduleFilter }
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setSelected(new Set());
    } catch (err) {
      console.error('Logboek laden mislukt:', err);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortDir, page, moduleFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/logs/chart-data');
      const mods = {};
      (res.data.modules || []).forEach(m => { mods[m.module] = m.count; });
      setModuleStats(mods);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const totalPages = Math.ceil(total / 20);

  const exportLogboek = () => {
    const wsData = [['Datum','Type','Module','Gebruiker','Bestandsnaam']];
    logs.forEach(l => {
      wsData.push([
        l.datum ? new Date(l.datum).toLocaleString('nl-NL') : '',
        l.type_export, l.module, l.gebruiker, l.bestandsnaam
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logboek');
    const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
    const a = document.createElement('a');
    a.href = `data:application/vnd.ms-excel;base64,${b64}`;
    a.download = 'Logboek_Export.xls';
    a.click();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllWithFiles = () => {
    const ids = logs.filter(l => l.has_file).map(l => l._id);
    setSelected(new Set(ids));
  };

  const downloadSelected = async () => {
    if (selected.size === 0) return;
    setDownloading(true);
    let count = 0;
    for (const id of selected) {
      try {
        const res = await api.get(`/api/logs/${id}/download`, { responseType: 'blob' });
        const log = logs.find(l => l._id === id);
        const filename = log?.bestandsnaam || 'export';
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        window.URL.revokeObjectURL(url);
        count++;
        if (selected.size > 1) await new Promise(r => setTimeout(r, 500));
      } catch (err) {
        const log = logs.find(l => l._id === id);
        toast.error(`Download mislukt: ${log?.bestandsnaam || id}`);
      }
    }
    setDownloading(false);
    if (count > 0) toast.success(`${count} ${count === 1 ? 'bestand' : 'bestanden'} gedownload`);
  };

  const downloadSingle = async (id) => {
    try {
      const log = logs.find(l => l._id === id);
      const res = await api.get(`/api/logs/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = log?.bestandsnaam || 'export'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${log?.bestandsnaam} gedownload`);
    } catch (err) {
      toast.error('Bestand niet meer beschikbaar');
    }
  };

  return (
    <div className="tw1-fade-in">
      <HeroBanner
        title="Boekingslogboek"
        subtitle={`${total} exports geregistreerd`}
        variant="default"
        icon={BookOpen}
        logos={[{ src: TW1_LOGO, alt: 'TW1' }]}
        actions={
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <Button onClick={downloadSelected} disabled={downloading} className="tw1-btn bg-[#1e8c45] hover:bg-[#176b37] text-white text-sm" size="sm" data-testid="download-selected-btn">
                <Download className="w-4 h-4 mr-1" /> {downloading ? 'Downloaden...' : `Download (${selected.size})`}
              </Button>
            )}
            {logs.length > 0 && (
              <Button onClick={exportLogboek} variant="outline" className="tw1-btn text-sm" size="sm">
                <Download className="w-4 h-4 mr-2" /> Exporteer .xls
              </Button>
            )}
          </div>
        }
      />

      {/* Module Filter Chips */}
      {Object.keys(moduleStats).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            variant={moduleFilter === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setModuleFilter(''); setPage(1); }}
            className={`text-xs rounded-full ${moduleFilter === '' ? 'bg-[#003366] hover:bg-[#00244d] text-white' : ''}`}
          >
            Alles ({Object.values(moduleStats).reduce((a, b) => a + b, 0)})
          </Button>
          {Object.entries(moduleStats).sort((a, b) => b[1] - a[1]).map(([mod, count]) => (
            <Button
              key={mod}
              variant={moduleFilter === mod ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setModuleFilter(moduleFilter === mod ? '' : mod); setPage(1); }}
              className={`text-xs rounded-full ${moduleFilter === mod ? 'text-white' : ''}`}
              style={moduleFilter === mod ? { backgroundColor: MODULE_COLORS[mod] || '#003366' } : {}}
            >
              {mod} ({count})
              {moduleFilter === mod && <X className="w-3 h-3 ml-1" />}
            </Button>
          ))}
        </div>
      )}

      <div className="tw1-card overflow-hidden" data-testid="logbook-table">
        {/* Search + selection controls */}
        <div className="p-4 border-b border-gray-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="logbook-search-input"
              placeholder="Zoeken in logboek..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 h-9 text-sm"
            />
          </div>
          {logs.some(l => l.has_file) && (
            <Button variant="ghost" size="sm" className="text-xs text-[#526071]" onClick={selectAllWithFiles} data-testid="select-all-files-btn">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> Selecteer beschikbaar
            </Button>
          )}
          {(search || moduleFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(''); setModuleFilter(''); setPage(1); }}
              className="text-xs text-[#526071]"
            >
              <X className="w-3 h-3 mr-1" /> Filters wissen
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full tw1-table">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-3 w-10"></th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('datum')}>
                  <span className="flex items-center gap-1">Datum <SortIcon field="datum" /></span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('type_export')}>
                  <span className="flex items-center gap-1">Type <SortIcon field="type_export" /></span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('module')}>
                  <span className="flex items-center gap-1">Module <SortIcon field="module" /></span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('gebruiker')}>
                  <span className="flex items-center gap-1">Gebruiker <SortIcon field="gebruiker" /></span>
                </th>
                <th className="px-4 py-3 text-left">Bestandsnaam</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#526071]">Laden...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-4 text-center">
                  <EmptyState
                    type={search || moduleFilter ? 'search' : 'docs'}
                    title={search || moduleFilter ? 'Geen resultaten gevonden' : 'Nog geen exports gelogd'}
                    subtitle={search || moduleFilter ? 'Probeer andere zoektermen of filters' : 'Exports verschijnen hier automatisch'}
                  />
                </td></tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={log._id || i} className={`${selected.has(log._id) ? 'bg-[#003366]/[0.03]' : ''}`}>
                    <td className="px-3 py-2.5 text-center">
                      {log.has_file && (
                        <Checkbox
                          checked={selected.has(log._id)}
                          onCheckedChange={() => toggleSelect(log._id)}
                          data-testid={`log-check-${i}`}
                        />
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-mono text-[#526071]">
                      {log.datum ? new Date(log.datum).toLocaleDateString('nl-NL', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#003366]/5 text-[#003366]">
                        {log.type_export}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge
                        variant="outline"
                        className="text-xs font-medium"
                        style={{ borderColor: (MODULE_COLORS[log.module] || '#003366') + '40', color: MODULE_COLORS[log.module] || '#003366' }}
                      >
                        {log.module}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-sm">{log.gebruiker}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-[#526071]">{log.bestandsnaam}</td>
                    <td className="px-3 py-2.5 text-center">
                      {log.has_file ? (
                        <button onClick={() => downloadSingle(log._id)} className="text-[#003366] hover:text-[#f5a800] transition-colors" title="Download" data-testid={`log-download-${i}`}>
                          <FileDown className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-gray-300" title="Bestand niet beschikbaar">
                          <FileDown className="w-4 h-4" />
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#526071]">
              {total} resultaten {moduleFilter && `in ${moduleFilter}`}
            </p>
            <div className="flex gap-1">
              {page > 1 && (
                <button onClick={() => setPage(p => p - 1)} className="px-3 h-8 rounded-lg text-xs font-medium text-[#526071] hover:bg-gray-100">
                  Vorige
                </button>
              )}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2;
                if (p < 1 || p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                      p === page ? 'bg-[#003366] text-white' : 'text-[#526071] hover:bg-gray-100'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              {page < totalPages && (
                <button onClick={() => setPage(p => p + 1)} className="px-3 h-8 rounded-lg text-xs font-medium text-[#526071] hover:bg-gray-100">
                  Volgende
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
